import { useEffect, useState } from 'react'
import { api, type PreparedInquiry } from '../../api/client'
import InquiryCallback from './InquiryCallback'
import InquiryFollowups from './InquiryFollowups'

const channelName = (channel: string) => ({email: 'Email', form: 'Website form', callback: 'Callback request', callback_request: 'Callback request', voice: 'Phone call'}[channel] ?? channel.replace(/_/g, ' '))
const message = (e: unknown) => e instanceof Error ? e.message : 'Could not load prepared inquiries'

function InquirySnapshot({companyId, inquiry}: {companyId: string; inquiry: PreparedInquiry}) {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<Awaited<ReturnType<typeof api.companyInquiry>> | null>(null)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    if (!open) return
    let live = true
    api.companyInquiry(companyId, inquiry.id).then(result => { if (live) { setData(result); setError('') } }).catch(e => { if (live) setError(message(e)) })
    return () => { live = false }
  }, [companyId, inquiry.id, open, attempt])
  return <details onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>View prepared package and criteria binding</summary>
    {error && <p className="notice crit" role="alert">{error}</p>}
    {open && !data && !error && <p className="muted">Loading saved package…</p>}
    {error && <button className="btn secondary small" onClick={() => { setError(''); setAttempt(value => value + 1) }}>Retry package</button>}
    {data && <>
      <details><summary>Criteria binding</summary>{data.inquiry.binding ? <pre className="compiled-prompt">{JSON.stringify(data.inquiry.binding, null, 2)}</pre> : <p className="muted">This company inquiry has no engagement binding.</p>}</details>
      <pre className="compiled-prompt">{JSON.stringify(data.package, null, 2)}</pre>
      <details><summary>Snapshot record</summary><pre className="compiled-prompt">{JSON.stringify(data.inquiry, null, 2)}</pre></details>
    </>}
  </details>
}

export default function CompanyInquiries({companyId}: {companyId: string}) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.companyInquiries>> | null>(null)
  const [offset, setOffset] = useState(0)
  const [refresh, setRefresh] = useState(0)
  const [error, setError] = useState('')
  useEffect(() => {
    let live = true
    let timer: number | undefined
    const reload = async () => {
      try {
        const result = await api.companyInquiries(companyId, offset)
        if (live) { setData(result); setError('') }
      } catch (e) { if (live) setError(message(e)) }
      finally { if (live) timer = window.setTimeout(reload, 10000) }
    }
    void reload()
    return () => { live = false; window.clearTimeout(timer) }
  }, [companyId, offset, refresh])
  function page(next: number) { setData(null); setError(''); setOffset(next) }
  return <section className="panel" style={{marginBlock: '1.5rem'}}>
    <h2>Prepared inquiries</h2>
    <p className="muted">These are saved package snapshots prepared before contacting the company. A snapshot does not prove an email was sent, a form was submitted or a call was placed. Check contact history for delivery outcomes.</p>
    {error && <p className="notice crit" role="alert">{error}</p>}
    {!data ? <p className="muted">{error ? 'Waiting to reload prepared inquiries…' : 'Loading prepared inquiries…'}</p> : <>
      {!data.rows.length && <p className="muted">No prepared inquiries on this page.</p>}
      <div className="workflow-list">{data.rows.map(inquiry => <article className="workflow-card" key={inquiry.id}>
        <div className="workflow-heading"><strong>{inquiry.engagement_name || 'Company inquiry'}</strong><span className="muted">{new Date(inquiry.created_at).toLocaleString()}</span></div>
        <p>Prepared for {channelName(inquiry.channel)}</p>
        <InquirySnapshot companyId={companyId} inquiry={inquiry} />
        <InquiryCallback companyId={companyId} inquiryId={inquiry.id} />
        <InquiryFollowups companyId={companyId} inquiryId={inquiry.id} />
      </article>)}</div>
      <div className="action-group" style={{marginTop: '1rem'}}>
        <button className="btn secondary small" disabled={offset === 0} onClick={() => page(Math.max(0, offset - 25))}>Newer inquiries</button>
        <span>{data.rows.length ? offset + 1 : 0}–{data.rows.length ? offset + data.rows.length : 0} of {data.total.toLocaleString()}</span>
        <button className="btn secondary small" disabled={offset + 25 >= data.total} onClick={() => page(offset + 25)}>Older inquiries</button>
      </div>
    </>}
    <button className="btn secondary small" style={{marginTop: '0.75rem'}} onClick={() => { setData(null); setError(''); setRefresh(value => value + 1) }}>Reload inquiries</button>
  </section>
}
